import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, MapPin, Calendar, ExternalLink, Utensils, Star, Clock, Users, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import BottomNav from "@/components/BottomNav";

// Kids Eat Free restaurant data - Summer 2026
const RESTAURANTS = [
  {
    id: 1,
    name: "Beefeater",
    offer: "Kids eat FREE with every adult main",
    days: "Monday - Friday",
    terms: "1 child per adult, under 16s",
    locations: "Nationwide",
    website: "https://www.beefeater.co.uk",
    category: "pub_restaurant",
    featured: true
  },
  {
    id: 2,
    name: "Brewers Fayre",
    offer: "Kids eat breakfast FREE",
    days: "Every day with breakfast",
    terms: "2 children per adult",
    locations: "Nationwide",
    website: "https://www.brewersfayre.co.uk",
    category: "pub_restaurant",
    featured: true
  },
  {
    id: 3,
    name: "Bella Italia",
    offer: "Kids eat for £1",
    days: "All day, every day",
    terms: "With adult main course",
    locations: "Nationwide",
    website: "https://www.bellaitalia.co.uk",
    category: "italian",
    featured: true
  },
  {
    id: 4,
    name: "Morrisons Cafe",
    offer: "Kids eat FREE",
    days: "All day, every day",
    terms: "With adult meal over £4.99",
    locations: "Morrisons stores",
    website: "https://www.morrisons.com",
    category: "cafe",
    featured: true
  },
  {
    id: 5,
    name: "Asda Cafe",
    offer: "Kids eat for £1",
    days: "All day, every day",
    terms: "Under 16s",
    locations: "Asda stores",
    website: "https://www.asda.com",
    category: "cafe",
    featured: false
  },
  {
    id: 6,
    name: "Tesco Cafe",
    offer: "Kids eat FREE",
    days: "All day, every day",
    terms: "With adult hot meal",
    locations: "Tesco Extra stores",
    website: "https://www.tesco.com",
    category: "cafe",
    featured: false
  },
  {
    id: 7,
    name: "Harvester",
    offer: "Kids eat for £1",
    days: "Monday - Friday, 4pm-6pm",
    terms: "With adult main, under 10s",
    locations: "Nationwide",
    website: "https://www.harvester.co.uk",
    category: "pub_restaurant",
    featured: true
  },
  {
    id: 8,
    name: "Sizzling Pubs",
    offer: "Kids eat FREE",
    days: "Monday - Thursday",
    terms: "1 child per adult main",
    locations: "Nationwide",
    website: "https://www.sizzlingpubs.co.uk",
    category: "pub_restaurant",
    featured: false
  },
  {
    id: 9,
    name: "Dunelm Pausa Cafe",
    offer: "Kids eat FREE",
    days: "School holidays",
    terms: "With adult meal over £5",
    locations: "Dunelm stores",
    website: "https://www.dunelm.com",
    category: "cafe",
    featured: false
  },
  {
    id: 10,
    name: "Yo! Sushi",
    offer: "Kids eat FREE",
    days: "Weekdays 3pm-5pm",
    terms: "With paying adult",
    locations: "Nationwide",
    website: "https://yosushi.com",
    category: "asian",
    featured: false
  },
  {
    id: 11,
    name: "The Real Greek",
    offer: "Kids eat FREE",
    days: "Weekends 12pm-5pm",
    terms: "2 kids per adult main",
    locations: "Nationwide",
    website: "https://therealgreek.com",
    category: "mediterranean",
    featured: false
  },
  {
    id: 12,
    name: "Ikea Restaurant",
    offer: "Kids eat FREE",
    days: "All day, every day",
    terms: "With adult meal, IKEA Family",
    locations: "All UK IKEA stores",
    website: "https://www.ikea.com/gb",
    category: "cafe",
    featured: true
  },
  {
    id: 13,
    name: "Las Iguanas",
    offer: "Kids eat for £1",
    days: "All day, every day",
    terms: "Under 12s with adult main",
    locations: "Nationwide",
    website: "https://www.iguanas.co.uk",
    category: "latin",
    featured: false
  },
  {
    id: 14,
    name: "Frankie & Bennys",
    offer: "Kids eat FREE",
    days: "After 5pm daily",
    terms: "1 child per adult main",
    locations: "Nationwide",
    website: "https://www.frankieandbennys.com",
    category: "italian",
    featured: true
  },
  {
    id: 15,
    name: "Hungry Horse",
    offer: "Kids eat FREE",
    days: "Monday - Friday",
    terms: "With adult main, under 16s",
    locations: "Nationwide",
    website: "https://www.hungryhorse.co.uk",
    category: "pub_restaurant",
    featured: false
  }
];

const CATEGORIES = [
  { value: "all", label: "All Places", emoji: "🍽️" },
  { value: "pub_restaurant", label: "Pub Restaurants", emoji: "🍺" },
  { value: "cafe", label: "Supermarket Cafes", emoji: "☕" },
  { value: "italian", label: "Italian", emoji: "🍝" },
  { value: "asian", label: "Asian", emoji: "🍱" },
  { value: "mediterranean", label: "Mediterranean", emoji: "🥙" },
  { value: "latin", label: "Latin American", emoji: "🌮" }
];

const KidsEatFree = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  const filteredRestaurants = RESTAURANTS.filter(r => {
    if (selectedCategory !== "all" && r.category !== selectedCategory) return false;
    if (showFeaturedOnly && !r.featured) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-yellow-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 text-white py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors">
            <ChevronLeft size={20} />
            Back to Home
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-4xl">
              🍽️
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Kids Eat Free
              </h1>
              <p className="text-white/90 text-lg">Summer 2026 Deals</p>
            </div>
          </div>
        </div>
      </div>

      {/* Intro Banner */}
      <div className="max-w-5xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-amber-200">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-amber-900 mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Save money on family meals this summer! 🌞
              </h2>
              <p className="text-amber-700">
                We&apos;ve compiled the best Kids Eat Free and low-cost meal deals across the UK. 
                Perfect for keeping the little ones fed during the school holidays without breaking the bank!
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl p-4 text-center border border-green-200">
              <p className="text-3xl font-bold text-green-700">{RESTAURANTS.length}</p>
              <p className="text-green-600 text-sm">Places Listed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 items-center">
          <Filter size={18} className="text-amber-600" />
          {CATEGORIES.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat.value
                  ? "bg-amber-500 text-white shadow-md"
                  : "bg-white text-amber-700 border border-amber-200 hover:bg-amber-50"
              }`}
            >
              {cat.emoji} {cat.label}
            </button>
          ))}
          <button
            onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
            className={`ml-auto px-4 py-2 rounded-full text-sm font-medium transition-all ${
              showFeaturedOnly
                ? "bg-yellow-500 text-white shadow-md"
                : "bg-white text-yellow-700 border border-yellow-200 hover:bg-yellow-50"
            }`}
          >
            <Star size={14} className="inline mr-1" /> Featured Only
          </button>
        </div>
      </div>

      {/* Restaurant Grid */}
      <div className="max-w-5xl mx-auto px-4 pb-24">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredRestaurants.map(restaurant => (
            <div
              key={restaurant.id}
              className={`bg-white rounded-2xl shadow-md overflow-hidden border-2 transition-all hover:shadow-lg ${
                restaurant.featured ? "border-amber-300" : "border-amber-100"
              }`}
            >
              {restaurant.featured && (
                <div className="bg-gradient-to-r from-amber-400 to-yellow-400 text-white text-xs font-medium px-3 py-1 flex items-center gap-1">
                  <Star size={12} className="fill-current" /> Featured Deal
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-amber-900" style={{ fontFamily: "Fredoka, sans-serif" }}>
                    {restaurant.name}
                  </h3>
                  <a
                    href={restaurant.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                  >
                    <ExternalLink size={18} />
                  </a>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 mb-3 border border-green-200">
                  <p className="font-bold text-green-700 flex items-center gap-2">
                    <Utensils size={16} />
                    {restaurant.offer}
                  </p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Calendar size={14} className="text-amber-500" />
                    <span>{restaurant.days}</span>
                  </div>
                  <div className="flex items-center gap-2 text-amber-700">
                    <Users size={14} className="text-amber-500" />
                    <span>{restaurant.terms}</span>
                  </div>
                  <div className="flex items-center gap-2 text-amber-700">
                    <MapPin size={14} className="text-amber-500" />
                    <span>{restaurant.locations}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredRestaurants.length === 0 && (
          <div className="text-center py-12">
            <p className="text-amber-600 text-lg">No restaurants found matching your filters.</p>
            <button
              onClick={() => { setSelectedCategory("all"); setShowFeaturedOnly(false); }}
              className="mt-4 text-amber-700 underline"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-8 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-2xl p-6 border border-amber-200">
          <h3 className="text-lg font-bold text-amber-900 mb-3" style={{ fontFamily: "Fredoka, sans-serif" }}>
            💡 Top Tips for Eating Out with Kids
          </h3>
          <ul className="space-y-2 text-amber-800">
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              <span>Always check the restaurant&apos;s website for the latest terms - offers can change!</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              <span>Some deals require you to download an app or sign up for a loyalty card</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              <span>Book ahead during school holidays - family restaurants get busy!</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              <span>Supermarket cafes are great for quick, affordable meals while shopping</span>
            </li>
          </ul>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-amber-600 text-sm mt-6">
          Deals correct as of July 2026. Always check with the venue directly as offers may vary by location and can change without notice.
        </p>
      </div>

      <BottomNav />
    </div>
  );
};

export default KidsEatFree;
